import React, { useEffect, useState } from "react";
import { Form, Input, Button, Select, DatePicker, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { style } from "../../Styles";
import { countryPhoneCode } from "../../Utils/country-phone-codes";
import dayjs from "dayjs";
import { useSelector, useDispatch } from "react-redux";
import { getCardholderByIdNumber } from "../../redux/cardSlice";

const SpecialCardForm = ({
  onSave,
  onSaveFailed,
  specialCard,
  onCancel,
  onDirty,
  unsavedChanges,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [dateRange, setDateRange] = useState(null);
  const [isValidRange, setIsValidRange] = useState(false);
  const [fieldsDisabled, setFieldsDisabled] = useState(true);
  const [showSearchButton, setShowSearchButton] = useState(true);
  const [searchingCardholder, setSearchingCardholder] = useState(false);
  const [foundCardholder, setFoundCardholder] = useState(null);
  const { RangePicker } = DatePicker;
  const { cardTypes } = useSelector((state) => state.cardTypes);

  const isCardExpired = specialCard?.cardValidTo
    ? dayjs().isAfter(dayjs(specialCard.cardValidTo), "day")
    : false;

  const initialValues = {
    cardNumber: specialCard?.cardNumber,
    name: specialCard?.cardHolder?.name,
    surname: specialCard?.cardHolder?.surname,
    idNumber: specialCard?.cardHolder?.identityCardNumber,
    email: specialCard?.cardHolder?.email,
    phoneCountryCode: specialCard?.cardHolder?.phoneCountryCode,
    phone: specialCard?.cardHolder?.phone,
    cardType:
      cardTypes?.length > 0
        ? cardTypes.find(
            (type) => type.id === specialCard?.populatedCardType?.id
          )?.id
        : undefined,
    cardValidRange: [
      dayjs(specialCard?.cardValidFrom),
      dayjs(specialCard?.cardValidTo),
    ],
  };

  const onSetValidityDate = (dates) => {
    setDateRange(dates);
  };

  const handleDateChange = (dates) => {
    if (dates) {
      const [startDate, endDate] = dates;

      const oneYearLater = startDate.clone().add(1, "year");

      if (endDate.isAfter(oneYearLater)) {
        message.warning(t("specialCardScreen.form.validityDateWarning"));
        onSetValidityDate([startDate, oneYearLater]);
        setIsValidRange(false);
      } else {
        onSetValidityDate(dates);
        setIsValidRange(true);
      }
    } else {
      onSetValidityDate(null);
    }
  };  useEffect(() => {
    if (specialCard) {
      // Use setTimeout to ensure form is ready
      setTimeout(() => {
        const formValues = {
          cardNumber: specialCard?.cardNumber,
          name: specialCard?.cardHolder?.name,
          surname: specialCard?.cardHolder?.surname,
          idNumber: specialCard?.cardHolder?.identityCardNumber,
          email: specialCard?.cardHolder?.email,
          phoneCountryCode: specialCard?.cardHolder?.phoneCountryCode,
          phone: specialCard?.cardHolder?.phone,
          cardType:
            cardTypes?.length > 0
              ? cardTypes.find(
                  (type) => type.id === specialCard?.populatedCardType?.id
                )?.id
              : undefined,
          cardValidRange: [
            dayjs(specialCard?.cardValidFrom),
            dayjs(specialCard?.cardValidTo),
          ],
          dateOfBirth: dayjs(specialCard?.cardHolder?.dateOfBirth),
        };

        // Reset form first to clear any previous state
        form.resetFields();

        // Set the form values
        form.setFieldsValue(formValues);

        // Force form to update
        form.validateFields();
      }, 100); // Increased timeout slightly      
      setIsValidRange(true);
      // If editing existing card, enable all fields and hide search button
      setFieldsDisabled(false);
      setShowSearchButton(false);
      setFoundCardholder(specialCard?.cardHolder);
    } else {
      // Reset when not editing
      setFieldsDisabled(true);
      setShowSearchButton(true);
      setFoundCardholder(null);
    }
  }, [specialCard, form, cardTypes]);

  const searchCardholder = async () => {
    const idNumber = form.getFieldValue("idNumber");
    if (!idNumber || !idNumber.trim()) {
      message.warning(t("specialCardScreen.form.pleaseEnterIdNumberFirst"));
      return;
    }

    setSearchingCardholder(true);
    try {
      const result = await dispatch(
        getCardholderByIdNumber(idNumber.trim())
      ).unwrap();
      // Cardholder found - pre-fill the form and enable fields
      setFoundCardholder(result);
      setFieldsDisabled(false);
      setShowSearchButton(false);

      form.setFieldsValue({
        name: result.name,
        surname: result.surname,
        email: result.email,
        phoneCountryCode: result.phoneCountryCode,
        phone: result.phone,
        dateOfBirth: dayjs(result.dateOfBirth),
      });

      message.success(t("specialCardScreen.form.cardholderFound"));    } catch (error) {
      // Check if it's a cardholder not found error
      const isCardholderNotFound =
        error.notFound ||
        error.message === "CARD_HOLDER_NOT_FOUND" ||
        (error.message && error.message.includes("CARD_HOLDER_NOT_FOUND"));

      if (isCardholderNotFound) {
        // Cardholder not found - enable all fields and hide search button
        setFieldsDisabled(false);
        setShowSearchButton(false);
        setFoundCardholder(null);
        message.info(t("specialCardScreen.form.cardholderNotFound"));
      } else {
        message.error(
          t("specialCardScreen.form.errorSearchingCardholder") +
            ": " +
            error.message
        );
      }
    }finally {
      setSearchingCardholder(false);
    }
  };

  const handleSubmit = (values) => {
    onSave(values);
  };
  return (
    <Form
      form={form}
      name="specialCard"
      layout="vertical"
      initialValues={initialValues}
      onFinish={handleSubmit}
      onFinishFailed={onSaveFailed}
      autoComplete="off"
      onValuesChange={() => onDirty()}
      key={specialCard ? `edit-${specialCard.id}` : "new"}
    >
      <Form.Item
        label={t("specialCardScreen.drawer.cardId")}
        name="cardNumber"
        rules={[
          {
            required: true,
            message: t("specialCardScreen.form.pleaseEnterCardId"),
          },
        ]}
      >
        <Input disabled={specialCard} />
      </Form.Item>{" "}      <Form.Item
        label={t("specialCardScreen.drawer.idNumber")}
        rules={[
          {
            required: true,
            message: t("specialCardScreen.form.pleaseEnterIdNumber"),
          },
        ]}
      >
        
      <Input.Group compact>
        <Form.Item
          name="idNumber"
          style={{ width: "calc(100% - 120px)", margin: 0 }}
        >
          <Input
            placeholder={
              t("specialCardScreen.form.idNumberPlaceholder") ||
              "Enter ID number"
            }
          />
        </Form.Item>
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={searchCardholder}
          loading={searchingCardholder}
          style={{ width: "120px" }}
        >
          {t("specialCardScreen.form.searchCardholder")}
        </Button>
      </Input.Group>
      </Form.Item>
      <Form.Item
        label={t("specialCardScreen.drawer.name")}
        name="name"
        rules={[
          {
            required: true,
            message: t("specialCardScreen.form.pleaseEnterName"),
          },
        ]}
      >
        <Input disabled={fieldsDisabled} />
      </Form.Item>
      <Form.Item
        label={t("specialCardScreen.drawer.surname")}
        name="surname"
        rules={[
          {
            required: true,
            message: t("specialCardScreen.form.pleaseEnterSurname"),
          },
        ]}
      >
        <Input disabled={fieldsDisabled} />
      </Form.Item>
      <Form.Item
        label={t("specialCardScreen.drawer.dateOfBirth")}
        name="dateOfBirth"
        rules={[
          {
            required: true,
            message: t("specialCardScreen.form.pleaseEnterDateOfBirth"),
          },
        ]}
      >
        <DatePicker
          style={{ ...style.headerButton, width: "100%" }}
          disabled={fieldsDisabled}
        />
      </Form.Item>
      <Form.Item
        label={t("specialCardScreen.drawer.email")}
        name="email"
        rules={[
          {
            required: false,
            type: "email",
            message: t("specialCardScreen.form.pleaseEnterValidEmail"),
          },
        ]}
      >
        <Input disabled={fieldsDisabled} />
      </Form.Item>{" "}
      <Form.Item
        label={t("specialCardScreen.drawer.phoneCountryCode")}
        name="phoneCountryCode"
        defaultValue={""}
        rules={[
          {
            required: true,
            message: t("userScreen.form.pleaseEnterPhoneCountryCode"),
          },
        ]}
      >
        <Select
          size="medium"
          showSearch
          optionFilterProp="filterBy"
          allowClear
          optionLabelProp="label"
          disabled={fieldsDisabled}
        >
          {countryPhoneCode.map((p) => (
            <Select.Option
              key={p.en}
              filterBy={`${p.en} +${p.phoneCode}`}
              value={p.phoneCode}
              label={
                <>
                  <span role="img">{p.emoji}</span>
                  {` +(${p.phoneCode})`}
                </>
              }
            >
              <span role="img">{p.emoji}</span>{" "}
              <span style={{ color: "gray" }}>{`+(${p.phoneCode})`}</span>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label={t("specialCardScreen.drawer.phone")}
        name="phone"
        rules={[
          {
            required: true,
            message: t("specialCardScreen.form.pleaseEnterPhone"),
          },
        ]}
      >
        <Input disabled={fieldsDisabled} />
      </Form.Item>
      <Form.Item
        label={t("specialCardScreen.drawer.cardType")}
        name="cardType"
        rules={[
          {
            required: true,
            message: t("specialCardScreen.form.pleaseSelectCardType"),
          },
        ]}
      >
        <Select disabled={!!specialCard && !isCardExpired}>
          {cardTypes?.length > 0 ? (
            cardTypes.map((type) => (
              <Select.Option key={type.id} value={type.id}>
                {type.name}
              </Select.Option>
            ))
          ) : (
            <Select.Option disabled>
              {t("specialCardScreen.form.noCardTypesAvailable")}
            </Select.Option>
          )}
        </Select>
      </Form.Item>
      <Form.Item
        label={t("specialCardScreen.drawer.cardValidity")}
        name="cardValidRange"
        rules={[
          {
            required: true,
            message: t("specialCardScreen.form.pleaseSelectCardValidityRange"),
          },
        ]}
      >
        <RangePicker
          style={{ ...style.headerButton, width: "100%" }}
          defaultValue={dateRange}
          format={"DD-MM-YYYY"}
          onChange={handleDateChange}
          allowClear={false}
          placeholder={[t("drawerFilter.startDate"), t("drawerFilter.endDate")]}
        />
      </Form.Item>
      <Form.Item style={{ float: "right" }}>
        <Button
          type="default"
          style={{ marginBottom: 32, marginRight: 8 }}
          onClick={() => onCancel(unsavedChanges)}
        >
          {t("specialCardScreen.form.cancel")}
        </Button>
        <Button
          type="primary"
          style={{ marginBottom: 32 }}
          htmlType="submit"
          disabled={!unsavedChanges || !isValidRange}
        >
          {!specialCard
            ? t("specialCardScreen.form.save")
            : t("specialCardScreen.form.update")}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default SpecialCardForm;
